import { selectFromRouteSubscription } from '../models';
import { SubscriptionHandler } from '../utils/createSubscription';
import syncRouteUpdatedHandler from './handlers/syncRouteUpdatedHandler';
import RouteOverviewQuery from '../queries/routes/RouteOverviewQuery';
import RouteDriverQuery from '../queries/routes/RouteDriverQuery';
import routeReversedHandler from './handlers/routeReversedHandler';
import routeReversingFailedHandler from './handlers/routeReversingFailedHandler';
import calendarRouteCompletedHandler from './handlers/calendarRouteCompletedHandler';
import routeRecallRevertedHandler from './handlers/routeRecallRevertedHandler';
import stagedEditDiscardedHandler from './handlers/stagedEditDiscardedHandler';
import unpublishedChangesDiscardedHandler from './handlers/unpublishedChangesDiscardedHandler';
import routeDriverUpdateHandler from './handlers/routeDriverUpdateHandler';

// Anything a planner can do to a published route has to bring unpublishedChanges back with it, or
// the republish affordance stays hidden and the driver never receives the edit. ETA_UPDATED
// (post-publish driver swap speed/break recalculation) resolves to RouteStopScheduledDto
// (ROUT-3031); moving and unscheduling a stop stay open until the driver arrives as of ROUT-3547.
const routeOverviewWithUnpublishedChanges = `${RouteOverviewQuery}
unpublishedChanges`;

const selector = selectFromRouteSubscription()
  .syncRouteUpdatedDto(b => b.route(RouteOverviewQuery))
  .routeStopSchedulingDto(b => b.route(route => route.id.uuid.isLocked.isScheduling.isUnscheduling))
  .routeStopSchedulingFailedDto(b => b.route(route => route.id.uuid.isLocked.isScheduling.isUnscheduling))
  .routeStopScheduledDto(b => b.route(routeOverviewWithUnpublishedChanges))
  .routeReOptimizingDto(b => b.route(route => route.id.uuid.isLocked.isScheduling.isUnscheduling))
  .routeReOptimizedDto(b => b.route(RouteOverviewQuery))
  .routeOptimizingDto(b => b.route(route => route.id.uuid.isLocked.isScheduling.isUnscheduling))
  .routeOptimizedDto(b => b.route(route => route.id.uuid.isLocked.isScheduling))
  .routeStopMovedDto(b => b.route(routeOverviewWithUnpublishedChanges))
  .routeStopMovingDto(b => b.route(route => route.id.uuid.isLocked))
  .routeStopUnschedulingDto(b => b.route(route => route.id.uuid.isLocked.isUnscheduling.isScheduling))
  .routeStopUnschedulingFailedDto(b => b.route(route => route.id.uuid.isLocked.isUnscheduling.isScheduling))
  .routeStopUnscheduledDto(b => b.route(routeOverviewWithUnpublishedChanges))
  .routeStopReschedulingDto(b => b.route(route => route.id.uuid.isLocked.isScheduling))
  .routeStopRescheduledDto(b => b.route(RouteOverviewQuery))
  .routeDriverUpdateDto(b => b.route(RouteDriverQuery))
  .routeDeletingFailedDto(b => b.route(route => route.id.name.uuid.startDate.projectId.isDeleting))
  .routeDispatchedDto(b => b.route(route => route.id.name.uuid.startDate.projectId.isDeleting.isDispatched.unpublishedChanges))
  .routeUnpublishingDto(b => b.route(route => route.id.uuid.isLocked))
  .routeUnpublishedDto(b => b.route(route => route.id.name.uuid.startDate.projectId.isDeleting.isDispatched.unpublishedChanges))
  .routeUnpublishingFailedDto(b => b.route(route => route.id.uuid.isDispatched))
  .routeRecallRevertedDto(b => b.route(route => route.id.name.uuid.startDate.projectId.isDeleting.isDispatched.unpublishedChanges))
  // unpublishedChanges comes along because the discard's own audit row is excluded from the badge: the
  // planner's remaining staged edits decide whether it stays raised, and only the server knows that.
  .routeStagedEditDiscardedDto(b => b.route(route => route.id.name.uuid.startDate.projectId.isDeleting.isDispatched.unpublishedChanges))
  .routeUnpublishedChangesDiscardedDto(b => b.route(route => route.id.name.uuid.startDate.projectId.isDeleting.isDispatched.unpublishedChanges.driver(driver => driver.name)))
  .routeOrderCompletedDto(b => b.route(RouteOverviewQuery))
  .routeArrivedAtStopDto(b => b.route(RouteOverviewQuery))
  .routeOnTheWayToStopDto(b => b.route(RouteOverviewQuery))
  .routeReversedDto(b => b.route(route => route.id.uuid.name.isReversing))
  .routeReversingDto(b => b.route(route => route.id.uuid.name.isReversing))
  .routeReversingFailedDto(b => b.route(route => route.id.uuid.name.isReversing))
  .toString();

export default (): SubscriptionHandler => ({
  name: 'RouteSubscription',
  selector,
  events: new Map(Object.entries({
    // Completion events — refresh calendar data + existing handlers
    SyncRouteUpdatedDto: [syncRouteUpdatedHandler, calendarRouteCompletedHandler],
    RouteStopMovedDto: [syncRouteUpdatedHandler, calendarRouteCompletedHandler],
    RouteStopRescheduledDto: [syncRouteUpdatedHandler, calendarRouteCompletedHandler],
    RouteStopScheduledDto: [syncRouteUpdatedHandler, calendarRouteCompletedHandler],
    RouteReOptimizedDto: [syncRouteUpdatedHandler, calendarRouteCompletedHandler],
    RouteStopUnscheduledDto: [calendarRouteCompletedHandler],
    RouteOptimizedDto: [calendarRouteCompletedHandler],
    RouteOrderCompletedDto: [syncRouteUpdatedHandler, calendarRouteCompletedHandler],
    RouteArrivedAtStopDto: [syncRouteUpdatedHandler],
    RouteOnTheWayToStopDto: [syncRouteUpdatedHandler],
    RouteReversedDto: [routeReversedHandler, calendarRouteCompletedHandler],
    RouteReverseFailedDto: [routeReversingFailedHandler, calendarRouteCompletedHandler],
    RouteRecallRevertedDto: [routeRecallRevertedHandler, calendarRouteCompletedHandler],
    // No calendar refresh: a reverted depot location changes neither completion nor the day's shape.
    RouteStagedEditDiscardedDto: [stagedEditDiscardedHandler],
    RouteUnpublishedChangesDiscardedDto: [unpublishedChangesDiscardedHandler],
    RouteDriverUpdateDto: [routeDriverUpdateHandler],
  })),
  onError(error: Error) {
    console.error('RouteSubscription Error, check if websocket client initialized', JSON.stringify(error));

  },
});
